import { Box, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import LockTwoToneIcon from '@mui/icons-material/LockTwoTone';
import { useTranslation } from 'react-i18next';
import { DeviceSettingLevel, ManagedDeviceSetting } from './deviceSettings';

/** "Managed by administrator — company" (plain text, for title attributes). */
export function managedSettingText(level: DeviceSettingLevel | null): string {
  if (level === 'device') return 'Managed by administrator — this device';
  if (level === 'branch') return 'Managed by administrator — branch';
  return 'Managed by administrator — company';
}

/** The same text through i18n (falls back to English when a language has no entry). */
export function useManagedSettingTitle(): (managed: ManagedDeviceSetting) => string {
  const { t } = useTranslation();
  return (managed) => t(`deviceSettings.managed.${managed.level ?? 'company'}`, managedSettingText(managed.level));
}

interface ManagedSettingLockProps {
  managed: ManagedDeviceSetting;
  sx?: SxProps<Theme>;
}

/**
 * The badge next to a control whose value the administrator manages (Developer Settings →
 * company, branch or device level). Renders nothing for an unmanaged setting.
 */
export default function ManagedSettingLock({ managed, sx }: ManagedSettingLockProps) {
  const titleFor = useManagedSettingTitle();
  if (!managed.locked) return null;
  const title = titleFor(managed);
  return (
    <Tooltip arrow title={title}>
      <Box
        component="span"
        role="img"
        aria-label={title}
        sx={{ display: 'inline-flex', alignItems: 'center', color: 'warning.main', lineHeight: 0, ...(sx as object) }}
      >
        <LockTwoToneIcon sx={{ fontSize: 16 }} />
      </Box>
    </Tooltip>
  );
}

/** The badge with its text on one line — for dialogs and menus. Nothing when unmanaged. */
export function ManagedSettingNote({ managed, sx }: ManagedSettingLockProps) {
  const titleFor = useManagedSettingTitle();
  if (!managed.locked) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'warning.main', ...(sx as object) }}>
      <LockTwoToneIcon sx={{ fontSize: 16 }} />
      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
        {titleFor(managed)}
      </Typography>
    </Box>
  );
}
